"use client";

import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { ColumnDef, PaginationState, SortingState } from "@tanstack/react-table";
import { useTranslations } from "next-intl";

import { AdminTableToolbar } from "@/components/admin/admin-table-toolbar";
import { AdminTableSerialOrderSelect } from "@/components/admin/admin-table-filter";
import {
  ADMIN_TABLE_PAGINATION_DEFAULTS,
  columnHasSerial,
  defaultCanBatchDelete,
  getAdminTablePageConfig,
  getAdminTableSerialSorting,
  shouldPassSerialOrderToDataTable,
  type AdminTableDataSerialBase,
  type AdminTablePagePreset,
  type AdminTableSerialMode,
  type AdminTableSerialOrder,
} from "@/components/admin/admin-table-utils";
import { DataTable } from "@/components/ui/data-table";
import type { DataTableSelectionAction } from "@/components/ui/data-table-row-actions";
import { cn } from "@/lib/utils";

type RowWithId = { id: string };

export type AdminPaginatedTableProps<TData extends RowWithId> = {
  columns: ColumnDef<TData, unknown>[];
  data: TData[];
  pagePreset?: AdminTablePagePreset;
  pageSize?: number;
  pageSizeOptions?: number[];
  showPagination?: boolean;
  showPageSizeSelector?: boolean;
  showPageJumpInput?: boolean;
  paginationResetKey?: unknown;
  isPending?: boolean;
  emptyState?: ReactNode;
  serialOrder?: AdminTableSerialOrder;
  onSerialOrderChange?: (order: AdminTableSerialOrder) => void;

  dataSerialBase?: AdminTableDataSerialBase;

  serialMode?: AdminTableSerialMode;
  showSerialOrderSelect?: boolean;
  addLabel?: string;
  onAdd?: () => void;

  onBatchDelete?: (ids: string[]) => void | Promise<void>;
  deleteLoading?: boolean;
  deleteLabel?: string;
  canBatchDelete?: (selectedIds: string[], totalCount: number) => boolean;
  trailing?: ReactNode;
  toolbarActions?: ReactNode;
  filters?: ReactNode;
  selectionResetToken?: number;
  selectionActionLabel?: string;
  selectionActionLoading?: boolean;
  onSelectionAction?: (rows: TData[]) => void | Promise<void>;

  selectionActions?: DataTableSelectionAction<TData>[];
  onReorder?: (rows: TData[]) => void | Promise<void>;
  manualPagination?: boolean;
  manualSorting?: boolean;
  defaultSorting?: SortingState;
  sorting?: SortingState;
  onSortingChange?: (sorting: SortingState) => void;
  padEmptyRows?: boolean;
  forceFixedBodyHeight?: boolean;

  fixedBodyRows?: number;
  rowCount?: number;
  pagination?: PaginationState;
  onPaginationChange?: (pagination: PaginationState) => void;

  tableMinWidth?: number;
};

function AdminPaginatedTableInner<TData extends RowWithId>({
  columns,
  data,
  pagePreset,
  pageSize: pageSizeProp,
  pageSizeOptions: pageSizeOptionsProp,
  showPagination = ADMIN_TABLE_PAGINATION_DEFAULTS.showPagination,
  showPageSizeSelector = ADMIN_TABLE_PAGINATION_DEFAULTS.showPageSizeSelector,
  showPageJumpInput = ADMIN_TABLE_PAGINATION_DEFAULTS.showPageJumpInput,
  paginationResetKey,
  isPending = false,
  emptyState,
  serialOrder: serialOrderProp,
  onSerialOrderChange,
  dataSerialBase: _dataSerialBase = "asc",
  serialMode: _serialMode = "page",
  showSerialOrderSelect,
  addLabel,
  onAdd,
  onBatchDelete,
  deleteLoading = false,
  deleteLabel,
  canBatchDelete = defaultCanBatchDelete,
  trailing,
  toolbarActions,
  filters,
  selectionResetToken,
  selectionActionLabel,
  selectionActionLoading = false,
  onSelectionAction,
  selectionActions,
  onReorder,
  manualPagination = false,
  manualSorting = false,
  defaultSorting: defaultSortingProp,
  sorting,
  onSortingChange,
  padEmptyRows,
  forceFixedBodyHeight,
  fixedBodyRows,
  rowCount,
  pagination,
  onPaginationChange,
  tableMinWidth,
}: AdminPaginatedTableProps<TData>) {
  const t = useTranslations("site.ui");
  const presetConfig = pagePreset ? getAdminTablePageConfig(pagePreset) : null;
  const pageSize = pageSizeProp ?? presetConfig?.pageSize;
  const defaultPageSize = presetConfig?.pageSize ?? pageSizeProp;
  const pageSizeOptions =
    pageSizeOptionsProp ??
    (presetConfig ? [...presetConfig.pageSizeOptions] : undefined);
  const hasSerialColumn = useMemo(() => columnHasSerial(columns), [columns]);
  const passSerialOrderToDataTable = useMemo(
    () => shouldPassSerialOrderToDataTable(columns),
    [columns],
  );
  const [internalSerialOrder, setInternalSerialOrder] =
    useState<AdminTableSerialOrder>("desc");
  const serialOrder = serialOrderProp ?? internalSerialOrder;
  const handleSerialOrderChange = onSerialOrderChange ?? setInternalSerialOrder;
  const shouldShowSerialOrderSelect =
    (showSerialOrderSelect ?? hasSerialColumn) && hasSerialColumn;

  const defaultSorting = useMemo(
    () =>
      defaultSortingProp ??
      (hasSerialColumn ? getAdminTableSerialSorting(serialOrder) : undefined),
    [defaultSortingProp, hasSerialColumn, serialOrder],
  );

  const selectedIdsRef = useRef<string[]>([]);
  const [deleteActive, setDeleteActive] = useState(false);
  const totalCount = rowCount ?? data.length;

  const showToolbar = Boolean((addLabel && onAdd) || trailing || toolbarActions);

  const syncDeleteActive = useCallback(
    (selectedIds: string[]) => {
      const nextDeleteActive = canBatchDelete(selectedIds, totalCount);
      setDeleteActive((current) =>
        current === nextDeleteActive ? current : nextDeleteActive,
      );
    },
    [canBatchDelete, totalCount],
  );

  const handleSelectionChange = useCallback(
    (rows: TData[]) => {
      const ids = rows.map((row) => row.id);
      selectedIdsRef.current = ids;
      syncDeleteActive(ids);
    },
    [syncDeleteActive],
  );

  useEffect(() => {
    syncDeleteActive(selectedIdsRef.current);
  }, [syncDeleteActive]);

  const resolvedSelectionActions = useMemo(() => {
    const actions: DataTableSelectionAction<TData>[] = selectionActions
      ? [...selectionActions]
      : onSelectionAction && selectionActionLabel
        ? [
            {
              label: selectionActionLabel,
              onAction: onSelectionAction,
              loading: selectionActionLoading,
              variant: "destructive",
            },
          ]
        : [];

    if (onBatchDelete) {
      actions.push({
        label: deleteLabel ?? t("delete"),
        variant: "destructive",
        loading: deleteLoading,
        disabled: !deleteActive,
        onAction: (rows) => onBatchDelete(rows.map((row) => row.id)),
      });
    }

    return actions;
  }, [
    selectionActions,
    onSelectionAction,
    selectionActionLabel,
    selectionActionLoading,
    onBatchDelete,
    deleteLabel,
    deleteLoading,
    deleteActive,
    t,
  ]);

  const toolbarTrailing = (
    <>
      {shouldShowSerialOrderSelect ? (
        <AdminTableSerialOrderSelect
          value={serialOrder}
          onChange={handleSerialOrderChange}
        />
      ) : null}
      {trailing}
    </>
  );

  return (
    <div className="flex flex-col gap-4">
      {showToolbar ? (
        <AdminTableToolbar
          addLabel={addLabel}
          onAdd={onAdd}
          trailing={toolbarTrailing}
          actions={toolbarActions}
        />
      ) : null}
      {!showToolbar && (filters || shouldShowSerialOrderSelect) ? (
        <div className="flex flex-wrap items-center gap-2">
          {filters}
          {shouldShowSerialOrderSelect && !showToolbar ? (
            <AdminTableSerialOrderSelect
              value={serialOrder}
              onChange={handleSerialOrderChange}
            />
          ) : null}
        </div>
      ) : (
        filters
      )}
      <div
        className={cn(
          "transition-opacity duration-150",
          isPending && "opacity-60",
        )}
      >
        <DataTable
          columns={columns}
          data={data}
          showPagination={showPagination}
          pageSize={pageSize}
          defaultPageSize={defaultPageSize}
          defaultSorting={defaultSorting}
          pageSizeOptions={pageSizeOptions}
          showPageSizeSelector={showPageSizeSelector}
          showPageJumpInput={showPageJumpInput}
          paginationResetKey={`${String(paginationResetKey ?? "")}:${serialOrder}`}
          serialOrder={
            passSerialOrderToDataTable ? serialOrder : undefined
          }
          getRowId={(row) => row.id}
          onSelectedRowsChange={handleSelectionChange}
          selectionResetToken={selectionResetToken}
          selectionActions={resolvedSelectionActions}
          onReorder={onReorder}
          isPending={isPending}
          emptyState={emptyState}
          manualPagination={manualPagination}
          manualSorting={manualSorting}
          sorting={sorting}
          onSortingChange={onSortingChange}
          padEmptyRows={padEmptyRows}
          forceFixedBodyHeight={forceFixedBodyHeight}
          fixedBodyRows={fixedBodyRows}
          rowCount={rowCount}
          pagination={pagination}
          onPaginationChange={onPaginationChange}
          tableMinWidth={tableMinWidth}
        />
      </div>
    </div>
  );
}

export const AdminPaginatedTable = memo(
  AdminPaginatedTableInner,
) as typeof AdminPaginatedTableInner;
